import { apiFetch } from "./apiFetch";
import { getAccessToken } from "./tokenStorage";

export interface ModelConfig {
	id: string;
	userId: string;
	name: string;
	description: string | null;
	generationType: string;
	providerType: string;
	modelIdentifier: string;
	modelDisplayName: string | null;
	isHosted: boolean;
	hasCredentials: boolean;
	connectionStatus: "pending" | "connected" | "error";
	connectionError: string | null;
	lastPingAt: string | null;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface CreateModelConfigData {
	name: string;
	description?: string | null;
	generationType: "text" | "image" | "video" | "audio";
	providerType: string;
	modelIdentifier: string;
	modelDisplayName?: string;
	isHosted?: boolean;
	credentials?: {
		apiKey?: string;
		baseUrl?: string;
		orgId?: string;
	};
	isActive?: boolean;
}

export interface UpdateModelConfigData {
	name?: string;
	description?: string | null;
	modelIdentifier?: string;
	modelDisplayName?: string;
	credentials?: {
		apiKey?: string;
		baseUrl?: string;
		orgId?: string;
	};
	isActive?: boolean;
}

export interface TestConnectionResult {
	success: boolean;
	responseTime: number;
	response?: string;
	error?: string;
	models?: Array<{ id: string; name: string }>;
}

export interface ProviderModel {
	id: string;
	name: string;
}

export interface EmbeddingModelConfig {
	id: string;
	name: string;
	modelIdentifier: string;
	providerType: string;
	modelDisplayName: string | null;
	isSystem: boolean;
	isEmbeddingModel: boolean;
}

export interface ListModelsParams {
	page?: number;
	limit?: number;
	search?: string;
	providerType?: string;
	generationType?: string;
	isActive?: boolean;
}

export interface ListModelConfigsResponse {
	success: boolean;
	data: ModelConfig[];
	meta: {
		page: number;
		limit: number;
		total: number;
		totalPages: number;
	};
}

export const modelConfigsService = {
	/**
	 * List all model configs owned by the current user
	 */
	async list(params: ListModelsParams = {}): Promise<ListModelConfigsResponse> {
		const queryParams = new URLSearchParams();
		if (params.page) queryParams.append("page", params.page.toString());
		if (params.limit) queryParams.append("limit", params.limit.toString());
		if (params.search) queryParams.append("search", params.search);
		if (params.providerType)
			queryParams.append("providerType", params.providerType);
		if (params.generationType)
			queryParams.append("generationType", params.generationType);
		if (params.isActive !== undefined)
			queryParams.append("isActive", params.isActive.toString());

		const queryString = queryParams.toString();
		const url = `/models${queryString ? `?${queryString}` : ""}`;

		// Use fetch directly since apiFetch extracts 'data' and we need the full response with meta
		const BASE_URL =
			import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";
		const accessToken = getAccessToken();
		const response = await fetch(`${BASE_URL}${url}`, {
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
			},
		});

		if (!response.ok) {
			const errorData = await response
				.json()
				.catch(() => ({ message: response.statusText }));
			throw new Error(
				errorData.message || `Failed to fetch models: ${response.statusText}`,
			);
		}

		const fullResponse = (await response.json()) as {
			success: boolean;
			data: ModelConfig[];
			meta: any;
		};
		return {
			success: fullResponse.success ?? true,
			data: fullResponse.data || [],
			meta: fullResponse.meta || {
				page: 1,
				limit: 20,
				total: 0,
				totalPages: 0,
			},
		};
	},

	/**
	 * Get a single model config by ID
	 */
	async get(id: string): Promise<{ success: boolean; data: ModelConfig }> {
		return await apiFetch<{ success: boolean; data: ModelConfig }>(
			`/models/${id}`,
		);
	},

	/**
	 * Create a new model config
	 */
	async create(
		data: CreateModelConfigData,
	): Promise<{ success: boolean; data: ModelConfig }> {
		return await apiFetch<{ success: boolean; data: ModelConfig }>("/models", {
			method: "POST",
			body: JSON.stringify(data),
		});
	},

	/**
	 * Update a model config
	 */
	async update(
		id: string,
		data: UpdateModelConfigData,
	): Promise<{ success: boolean; data: ModelConfig }> {
		return await apiFetch<{ success: boolean; data: ModelConfig }>(
			`/models/${id}`,
			{
				method: "PUT",
				body: JSON.stringify(data),
			},
		);
	},

	/**
	 * Delete a model config
	 */
	async delete(id: string): Promise<void> {
		await apiFetch(`/models/${id}`, {
			method: "DELETE",
		});
	},

	/**
	 * Test model config connection
	 */
	async testConnection(
		id: string,
		prompt?: string,
	): Promise<TestConnectionResult> {
		return await apiFetch<TestConnectionResult>(
			`/models/${id}/test-connection`,
			{
				method: "POST",
				body: JSON.stringify({ prompt }),
			},
		);
	},

	/**
	 * List available models from provider
	 */
	async listProviderModels(id: string): Promise<ProviderModel[]> {
		return await apiFetch<ProviderModel[]>(`/models/${id}/provider-models`);
	},

	/**
	 * List all available embedding models
	 */
	async listEmbeddingModels(): Promise<EmbeddingModelConfig[]> {
		return await apiFetch<EmbeddingModelConfig[]>("/models/embedding-models");
	},
};
